// hooks/useQueryManager.js
import { useState, useCallback, useEffect } from 'react';

// Default empty slots for custom queries
const DEFAULT_CUSTOM_SLOTS = {
  custom1: { id: 'custom1', name: 'Custom Query 1', text: "", type: "custom", isCustom: true, isEmpty: true, lastUsed: null },
  custom2: { id: 'custom2', name: 'Custom Query 2', text: "", type: "custom", isCustom: true, isEmpty: true, lastUsed: null },
  custom3: { id: 'custom3', name: 'Custom Query 3', text: "", type: "custom", isCustom: true, isEmpty: true, lastUsed: null }
};

export const useQueryManager = (sendButtonUpdate, chatService, selectedQueryFromParent, setSelectedItemsFromParent) => {
  const [queries, setQueries] = useState({
    predefined: {}, 
    custom: DEFAULT_CUSTOM_SLOTS
  });
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [editingQuery, setEditingQuery] = useState(null);

  // 1. Sync with parent component's selected query
  useEffect(() => {
    if (selectedQueryFromParent && selectedQueryFromParent !== selectedQuery) {
      if (selectedQueryFromParent.startsWith('custom')) {
        setSelectedQuery(selectedQueryFromParent);
      }
    }
  }, [selectedQueryFromParent, selectedQuery]);

  // 2. Load Queries from Backend (The Source of Truth)
  useEffect(() => {
    const loadBackendQueries = async () => {
      if (!chatService) return;
      
      try {
        const response = await chatService.getQueries();
        
        if (response.success && response.queries) {
          const predefinedData = {};
          const backendCustomData = { ...DEFAULT_CUSTOM_SLOTS };

          response.queries.forEach(q => {
            if (q.isCustom) {
              if (backendCustomData[q.id]) {
                backendCustomData[q.id] = {
                  id: q.id,
                  name: q.name || q.id,
                  text: q.text || q.query || "",
                  type: "custom",
                  isCustom: true,
                  isEmpty: false, 
                  lastUsed: q.lastUsed || null
                };
              }
            } else {
              predefinedData[q.id] = q;
            }
          });

          setQueries({
            predefined: predefinedData,
            custom: backendCustomData
          });
        }
      } catch (error) {
        console.error('❌ Failed to load queries:', error);
      }
    };

    loadBackendQueries();
  }, [chatService]);


  // 3. Helper: Get ordered list for UI
  const getOrderedQueries = useCallback(() => {
    const allQueries = { ...queries.predefined, ...queries.custom };
    const entries = Object.entries(allQueries);
    
    const sortFn = ([, a], [, b]) => {
      if (!a.lastUsed && !b.lastUsed) return 0;
      if (!a.lastUsed) return 1;
      if (!b.lastUsed) return -1;
      return new Date(b.lastUsed) - new Date(a.lastUsed);
    };

    if (selectedQuery && allQueries[selectedQuery]) {
      const selectedEntry = [selectedQuery, allQueries[selectedQuery]];
      const otherEntries = entries.filter(([key]) => key !== selectedQuery);
      return [selectedEntry, ...otherEntries.sort(sortFn)];
    }
    return entries.sort(sortFn);
  }, [queries, selectedQuery]);


  // 4. Handle Selection
  const handleQuerySelect = useCallback(async (queryKey) => {
    const query = queries.predefined[queryKey] || queries.custom[queryKey];
    if (!query) return;

    const now = new Date().toISOString();
    
    setQueries(prev => {
      const category = prev.predefined[queryKey] ? 'predefined' : 'custom';
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [queryKey]: { ...prev[category][queryKey], lastUsed: now }
        }
      };
    });

    setSelectedQuery(queryKey);
    if (setSelectedItemsFromParent) {
      setSelectedItemsFromParent(prev => ({ ...prev, query: queryKey }));
    }
    
    if (chatService) {
      chatService.notifyQueryChanged(queryKey).catch(e => console.error("Sync error", e));
    }
    
    sendButtonUpdate('query_changed', { queryId: queryKey, queryKey });
  }, [queries, sendButtonUpdate, chatService, setSelectedItemsFromParent]);


  // 5. Handle Editing -> Sync to Backend
  const handleQueryEdit = useCallback(async (queryKey, currentText) => {
    const query = queries.custom[queryKey];
    if (!query || query.type !== 'custom') return;

    let newName = query.name;
    let newText = null;

    if (query.isEmpty) {
      newText = prompt('Enter your custom query:', '');
    } else if (editingQuery === queryKey || !query.isEmpty) {
      newText = prompt('Edit your custom query:', currentText);
    }

    if (newText === null) {
      setEditingQuery(null);
      return;
    }

    const trimmedText = newText.trim();
    const now = new Date().toISOString();

    // --- CASE A: DELETE (User cleared text) ---
    if (trimmedText === "") {
      const resetQuery = { ...DEFAULT_CUSTOM_SLOTS[queryKey] };
      
      setQueries(prev => ({
        ...prev,
        custom: { ...prev.custom, [queryKey]: resetQuery }
      }));
      setEditingQuery(null);

      if (chatService) {
        try {
          // Fixed: Passes queryKey as the ID
          await chatService.notifyCustomQueryDeleted(queryKey);
          console.log(`✅ Backend: Deleted ${queryKey}`);
        } catch (e) { console.error('❌ Sync delete failed', e); }
      }
      return;
    }

    // --- CASE B: CREATE / UPDATE ---
    const updatedQuery = {
      ...query,
      text: trimmedText,
      isEmpty: false,
      lastUsed: now
    };

    setQueries(prev => ({
      ...prev,
      custom: { ...prev.custom, [queryKey]: updatedQuery }
    }));
    
    setSelectedQuery(queryKey);
    if (setSelectedItemsFromParent) {
      setSelectedItemsFromParent(prev => ({ ...prev, query: queryKey }));
    }
    setEditingQuery(null);

    if (chatService) {
      try {
        if (query.isEmpty) {
           await chatService.notifyCustomQueryCreated(newName, trimmedText, queryKey);
        } else {
           // Fixed: Passes 4 args. The 4th arg (queryKey) is what backend uses as ID
           await chatService.notifyCustomQueryEdited(newName, currentText, trimmedText, queryKey);
        }
        console.log(`✅ Backend: Updated ${queryKey}`);
      } catch (e) { console.error('❌ Sync update failed', e); }
    }

    sendButtonUpdate(query.isEmpty ? 'custom_query_created' : 'custom_query_edited', {
      queryId: queryKey,
      queryKey,
      queryText: trimmedText
    });

  }, [queries, editingQuery, sendButtonUpdate, chatService, setSelectedItemsFromParent]);


  const handleQueryClick = useCallback((queryKey, event) => {
    const query = queries.predefined[queryKey] || queries.custom[queryKey];
    if (!query) return;

    if (!query.isCustom) {
      handleQuerySelect(queryKey);
    } else {
      if (query.isEmpty) {
        handleQueryEdit(queryKey, "");
      } else if (editingQuery === queryKey || event.detail === 2) {
        handleQueryEdit(queryKey, query.text);
      } else {
        handleQuerySelect(queryKey);
      }
    }
  }, [queries, editingQuery, handleQuerySelect, handleQueryEdit]);

  const clearEditingState = useCallback(() => setEditingQuery(null), []);

  const getQueryDisplayName = useCallback((key) => {
    const q = queries.custom[key] || queries.predefined[key];
    return q ? (q.name || key) : key;
  }, [queries]);

  return {
    queries: getOrderedQueries(),
    selectedQuery,
    editingQuery,
    handleQueryClick,
    clearEditingState,
    getQueryDisplayName
  };
};